var _locale = 'en-US';
/**
 * Helper for getting year and month from date string.
 * If called with no arguments, uses today's date.
 *
 * @param {String} dateString                Date string in yyyy-mm-dd format
 * @param {Date} date    Fallback date if dateString is falsy. Defaults to today's date.
 * @param {Object} props {month, year} If given, it will be used instead of date and dateString
 * @return {Object} {month, year}
 */
var getMonthYear = function (dateString, date, props) {
    if (typeof (props === null || props === void 0 ? void 0 : props.month) === 'number' && typeof props.year === 'number') {
        return { month: props.month, year: props.year };
    }
    if (dateString) {
        var dateElems = dateString.split('-');
        return { month: Number(dateElems[1]) - 1, year: Number(dateElems[0]) };
    }
    if (date) {
        return {
            month: date.getMonth(),
            year: date.getFullYear()
        };
    }
    var today = new Date();
    return {
        month: today.getMonth(),
        year: today.getFullYear()
    };
};
/**
 * Helper for getting string with a language sensitive representation of the date portion of this date.
 *
 * @param {String} dateString   Date string in yyyy-mm-dd format
 * @param {String} locale   Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Intl.DateTimeFormatOptions} options  Date.prototype.toLocaleDateString() options ( https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @return {String} Language sensitive representation of the date
 */
var getLocaleDateString = function (dateString, locale, options) {
    // eslint-disable-next-line no-param-reassign
    locale = locale || _locale;
    if (!dateString) {
        return '';
    }
    if (locale !== _locale) {
        _locale = locale;
    }
    var parts = dateString.split('-');
    var dateObj = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return dateObj.toLocaleDateString(locale, options);
};
/**
 * Helper for getting language sensitive representation of the date range.
 *
 * @param {String} startDateString   Date string in yyyy-mm-dd format
 * @param {String} endDateString   Date string in yyyy-mm-dd format
 * @param {String} locale   Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Intl.DateTimeFormatOptions} options  Date.prototype.toLocaleDateString() options ( https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @return {String} Language sensitive representation of the dates
 */
var getLocaleConsolidatedDateString = function (startDateString, endDateString, locale, options) {
    if (!startDateString || !endDateString) {
        return getLocaleDateString(startDateString || endDateString, locale, options);
    }
    var startDate = getLocaleDateString(startDateString, locale, options);
    var endDate = getLocaleDateString(endDateString, locale, options);
    // Use "mmm dd - mmm dd" or "mm/dd/yyyy - mm/dd/yyyy"
    return "".concat(startDate, " - ").concat(endDate);
};
/**
 * Returns string representation of a date when given a date object.
 * The result is in the format "YYYY-MM-DD".
 *
 * @param {Date} date - date object
 * @return {string}  string representation of date.
 */
var getDateToString = function (date) {
    var month = date.getMonth() + 1;
    month = month < 10 ? "0".concat(month) : month;
    var day = date.getDate();
    day = day < 10 ? "0".concat(day) : day;
    return "".concat(date.getFullYear(), "-").concat(month, "-").concat(day);
};
/**
 * Compares 2 date strings and returns an integer that indicates whether the
 * second date is earlier (-1), later (1), or equal to (0) the first.
 *
 * @param {string} startString - start date string in format, "YYYY-MM-DD"
 * @param {string} endString - end date string in format, "YYYY-MM-DD"
 * @return {int}  indication or comparison
 */
var compareDateStrings = function (startString, endString) {
    if (startString === void 0) { startString = ''; }
    if (endString === void 0) { endString = ''; }
    if (!startString || !endString)
        return 0;
    if (startString === endString) {
        return 0;
    }
    var difference = Date.parse(endString) - Date.parse(startString);
    if (difference > 0) {
        return 1;
    }
    if (difference < 0) {
        return -1;
    }
    return 0;
};
var isSameDate = function (startString, endString) {
    return startString && endString ? startString === endString : false;
};
var isEarlierDate = function (startString, endString) {
    return startString && endString ? compareDateStrings(startString, endString) === -1 : false;
};
var isLaterDate = function (startString, endString) {
    return startString && endString ? compareDateStrings(startString, endString) === 1 : false;
};
/**
 * Returns the formatted version of the YYYY-MM-DD date string.
 *
 * @param {String} dateString    Date string in yyyy-mm-dd format
 * @param {String} locale        Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Intl.DateTimeFormatOptions} options       Date.prototype.toLocaleDateString() options (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @returns {String}             Language sensitive representation of the date OR override
 */
var getEffectiveDateText = function (dateString, locale, options) { return getLocaleDateString(dateString, locale, options); };
/**
 * Helper for getting language sensitive representation of the date range
 *
 * @param startDate         Start date string in yyyy-mm-dd format
 * @param endDate           End date string in yyyy-mm-dd format
 * @param locale            Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Intl.DateTimeFormatOptions} options           Date.prototype.toLocaleDateString() options (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @returns {String|string}  Language sensitive representation of the dates with overrides if needed
 */
var getEffectiveConsolidatedDateString = function (startDate, endDate, locale, options) {
    if (startDate || endDate) {
        var effectiveStartDate = getEffectiveDateText(startDate, locale, options);
        var effectiveEndDate = getEffectiveDateText(endDate, locale, options);
        return "".concat(effectiveStartDate, " - ").concat(effectiveEndDate);
    }
    return getLocaleConsolidatedDateString(startDate, endDate, locale, options);
};

export { compareDateStrings, getDateToString, getEffectiveConsolidatedDateString, getEffectiveDateText, getLocaleConsolidatedDateString, getLocaleDateString, getMonthYear, isEarlierDate, isLaterDate, isSameDate };
